import { Chart, registerables, type Plugin } from "chart.js";
import { translator } from "../../translation/translator";
import type { DashboardStats } from "./dashboard-api";

Chart.register(...registerables);

// Draws the value of a dataset's highest point directly on the chart, so the
// peak is readable without hovering for the tooltip (requested by ops —
// hover-only values were easy to miss when scanning several charts at once).
const maxValueLabelPlugin: Plugin<"line" | "bar"> = {
    id: "maxValueLabel",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
            // Skip datasets toggled off via the legend (see
            // toggleLegendDataset) — their peak label shouldn't linger once
            // the line itself is hidden.
            if (!chart.isDatasetVisible(datasetIndex)) {
                return;
            }

            const data = dataset.data as Array<number | null | undefined>;
            if (!Array.isArray(data) || data.length === 0) {
                return;
            }

            let maxIndex = -1;
            let maxValue = 0;
            data.forEach((value, index) => {
                if (typeof value === "number" && value > maxValue) {
                    maxValue = value;
                    maxIndex = index;
                }
            });
            // All-zero series: nothing meaningful to highlight.
            if (maxIndex === -1) {
                return;
            }

            const point = chart.getDatasetMeta(datasetIndex).data[maxIndex];
            if (!point) {
                return;
            }

            const { x, y } = point.getProps(["x", "y"], true);
            ctx.save();
            ctx.font = "bold 11px sans-serif";
            ctx.fillStyle = "#333333";
            ctx.textAlign = "center";
            ctx.fillText(maxValue.toLocaleString(), x, y - 8);
            ctx.restore();
        });
    },
};

Chart.register(maxValueLabelPlugin);

// Force at least a 2x backing-store resolution regardless of what
// window.devicePixelRatio reports — on some Windows display-scaling setups
// Chart.js's auto-detected ratio still leaves lines/text visibly blurry.
const CHART_DEVICE_PIXEL_RATIO = Math.max(window.devicePixelRatio || 1, 2);

interface TrendPoint {
    bucket: string;
    count: number;
}

interface TrendSeries {
    label: string;
    translateKey: string;
    points: TrendPoint[];
}

// One color per line when a chart combines several trend metrics (e.g.
// Registrations/Updates/Likes/Reads on the same canvas — see renderOne()).
const TREND_LINE_COLORS = ["#4a7ebb", "#e08a3c", "#6a9955", "#a15bb0"];
const TREND_FILL_COLORS = [
    "rgba(74, 126, 187, 0.15)",
    "rgba(224, 138, 60, 0.15)",
    "rgba(106, 153, 85, 0.15)",
    "rgba(161, 91, 176, 0.15)",
];

export class DashboardRender {
    private readonly charts = new Map<HTMLCanvasElement, Chart>();

    public renderStats(statEls: HTMLElement[], stats: DashboardStats): void {
        for (const el of statEls) {
            const key = el.dataset.stat;
            if (!key) {
                continue;
            }
            const value = this.resolveNumericValue(key, stats);
            el.textContent = value === null ? "—" : value.toLocaleString();
        }
    }

    public renderDiffs(diffEls: HTMLElement[], stats: DashboardStats): void {
        for (const el of diffEls) {
            const key = el.dataset.stat;
            if (!key) {
                continue;
            }
            const value = this.resolveNumericValue(key, stats);
            if (value === null) {
                el.textContent = "—";
                el.classList.remove("is-positive", "is-negative");
                continue;
            }
            if (value > 0) {
                el.textContent = `+${value.toLocaleString()}`;
            } else if (value < 0) {
                el.textContent = value.toLocaleString();
            } else {
                el.textContent = "±0";
            }
            el.classList.toggle("is-positive", value > 0);
            el.classList.toggle("is-negative", value < 0);
        }
    }

    public renderCharts(
        canvases: HTMLCanvasElement[],
        stats: DashboardStats,
        resolveLegendEl: (canvas: HTMLCanvasElement) => HTMLElement | null,
    ): void {
        for (const canvas of canvases) {
            this.renderOne(canvas, stats, resolveLegendEl);
        }
    }

    // Explicit per-status links alongside the status_breakdown bar chart —
    // the chart's own onClick-to-filter (see renderStatusBreakdown) isn't
    // discoverable on its own, so this surfaces the same status filter URLs
    // as plain, visible links.
    public renderStatusLinks(lists: HTMLElement[], stats: DashboardStats): void {
        for (const list of lists) {
            const chartKey = list.dataset.chart;
            if (!chartKey) {
                continue;
            }

            const [section, metric] = chartKey.split(".");
            const sectionData = this.getSection(stats, section);
            const breakdown = sectionData ? sectionData[metric] : null;

            while (list.firstChild) {
                list.removeChild(list.firstChild);
            }

            if (!breakdown || typeof breakdown !== "object") {
                continue;
            }

            const linkBase = list.dataset.statusLinkBase;
            const statusParam = list.dataset.statusParam ?? "status";
            const translatePrefix = list.dataset.statusTranslatePrefix ?? "";

            for (const [status, count] of Object.entries(breakdown as Record<string, number>)) {
                const label = translatePrefix ? translator.translate(`${translatePrefix}${status}`) : status;

                const link = document.createElement("a");
                // Reuses the same `.badge.status[data-status]` styling as the
                // product/brand card status badges (see common/_badges.scss)
                // so the color coding matches what's already shown on the
                // list/detail pages for that status.
                link.className = "admin-dashboard__status-link badge status";
                link.dataset.status = status;
                link.textContent = `${label} (${count.toLocaleString()})`;
                link.target = "_blank";
                link.rel = "noopener";
                if (linkBase) {
                    const url = new URL(linkBase, window.location.origin);
                    url.searchParams.set(statusParam, status);
                    link.href = url.toString();
                } else {
                    link.href = "#";
                }

                const item = document.createElement("li");
                item.appendChild(link);
                list.appendChild(item);
            }
        }
    }

    // search_keywords renders up to 3 month-rows (newest first), each with a
    // fixed-order 3-column grid (brand/category/keyword — see
    // DashboardService::SEARCH_KEYWORD_RANKING_DISPLAY_TYPES) instead of a
    // chart/single stat — data-stat points at the months array itself
    // ("search_keywords.months"). How many months have data isn't known at
    // template time, so the whole structure is built here.
    public renderRankingMonths(containers: HTMLElement[], stats: DashboardStats): void {
        for (const container of containers) {
            const key = container.dataset.stat;
            if (!key) {
                continue;
            }

            const months = this.resolveArrayValue(key, stats);

            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }

            if (!months) {
                continue;
            }

            for (const month of months) {
                if (!month || typeof month !== "object") {
                    continue;
                }
                const monthRow = month as Record<string, unknown>;
                const groups = Array.isArray(monthRow.groups) ? monthRow.groups : [];
                const monthsAgo = typeof monthRow.months_ago === "number" ? monthRow.months_ago : null;

                const monthBlock = document.createElement("div");
                monthBlock.className = "admin-dashboard__ranking-month";

                const monthTitle = document.createElement("h3");
                monthTitle.className = "admin-dashboard__ranking-month-title";
                if (monthsAgo === 1) {
                    monthTitle.dataset.translate = "admin_dashboard_last_month";
                    monthTitle.textContent = translator.translate("admin_dashboard_last_month");
                } else {
                    monthTitle.dataset.translate = "admin_dashboard_months_ago";
                    monthTitle.textContent = translator.translate("admin_dashboard_months_ago", {
                        n: monthsAgo ?? "",
                    });
                }
                monthBlock.appendChild(monthTitle);

                const columns = document.createElement("div");
                columns.className = "admin-dashboard__ranking-month-columns";
                for (const group of groups) {
                    columns.appendChild(this.renderRankingGroupColumn(group));
                }
                monthBlock.appendChild(columns);

                container.appendChild(monthBlock);
            }
        }
    }

    private renderRankingGroupColumn(group: unknown): HTMLElement {
        const block = document.createElement("div");
        block.className = "admin-dashboard__ranking-group";

        if (!group || typeof group !== "object") {
            return block;
        }
        const groupRow = group as Record<string, unknown>;
        const searchType = typeof groupRow.search_type === "string" ? groupRow.search_type : "";
        const items = Array.isArray(groupRow.items) ? groupRow.items : [];

        const heading = document.createElement("h4");
        heading.className = "admin-dashboard__ranking-group-title";
        const translateKey = `admin_dashboard_search_type_${searchType}`;
        heading.dataset.translate = translateKey;
        heading.textContent = translator.translate(translateKey);
        block.appendChild(heading);

        const list = document.createElement("ol");
        list.className = "admin-dashboard__ranking-list";

        for (const item of items) {
            if (!item || typeof item !== "object") {
                continue;
            }
            const row = item as Record<string, unknown>;
            const li = document.createElement("li");
            li.className = "admin-dashboard__ranking-row";

            const rank = document.createElement("span");
            rank.className = "admin-dashboard__ranking-rank";
            rank.textContent = typeof row.rank === "number" ? `#${row.rank}` : "";

            const keyword = typeof row.search_keyword === "string" ? row.search_keyword : "";
            const resolvedLabel = typeof row.search_label === "string" ? row.search_label : "";
            const url = typeof row.search_url === "string" ? row.search_url : "";

            const label = document.createElement(url ? "a" : "span");
            label.className = "admin-dashboard__ranking-label";
            // search_label is the resolved brand/category name — shown
            // alongside the raw id so it stays unambiguous.
            label.textContent = resolvedLabel ? `${resolvedLabel} (${keyword})` : keyword;
            if (url && label instanceof HTMLAnchorElement) {
                label.href = url;
                label.target = "_blank";
                label.rel = "noopener";
            }

            const count = document.createElement("span");
            count.className = "admin-dashboard__ranking-count";
            count.textContent = typeof row.search_count === "number" ? row.search_count.toLocaleString() : "";

            li.appendChild(rank);
            li.appendChild(label);
            li.appendChild(count);
            list.appendChild(li);
        }

        block.appendChild(list);

        return block;
    }

    public destroyAll(): void {
        for (const chart of this.charts.values()) {
            chart.destroy();
        }
        this.charts.clear();
    }

    private resolveNumericValue(dottedKey: string, stats: DashboardStats): number | null {
        const [section, metric] = dottedKey.split(".");
        const sectionData = this.getSection(stats, section);
        if (!sectionData) {
            return null;
        }
        const value = sectionData[metric];
        return typeof value === "number" ? value : null;
    }

    private resolveArrayValue(dottedKey: string, stats: DashboardStats): unknown[] | null {
        const [section, metric] = dottedKey.split(".");
        const sectionData = this.getSection(stats, section);
        if (!sectionData) {
            return null;
        }
        const value = sectionData[metric];
        return Array.isArray(value) ? value : null;
    }

    private getSection(stats: DashboardStats, section: string): Record<string, unknown> | null {
        const value = (stats as unknown as Record<string, unknown>)[section];
        return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
    }

    private renderOne(
        canvas: HTMLCanvasElement,
        stats: DashboardStats,
        resolveLegendEl: (canvas: HTMLCanvasElement) => HTMLElement | null,
    ): void {
        const chartKey = canvas.dataset.chart;
        if (!chartKey) {
            return;
        }

        const metricKeys = chartKey
            .split(",")
            .map((key) => key.trim())
            .filter(Boolean);
        if (metricKeys.length === 0) {
            return;
        }

        const existing = this.charts.get(canvas);
        if (existing) {
            existing.destroy();
            this.charts.delete(canvas);
        }

        // status_breakdown is always a single-metric bar chart — everything
        // else is one or more trend lines combined on one canvas (e.g.
        // Registrations + Updates, or + Likes/Reads for the post sections).
        if (canvas.dataset.chartType === "bar") {
            const [section, metric] = metricKeys[0].split(".");
            const sectionData = this.getSection(stats, section);
            const breakdown = sectionData ? sectionData[metric] : null;
            if (breakdown && typeof breakdown === "object") {
                this.renderStatusBreakdown(canvas, breakdown as Record<string, number>);
            }
            return;
        }

        const labelKeys = (canvas.dataset.chartLabels ?? "").split(",").map((key) => key.trim());

        const series: TrendSeries[] = [];
        metricKeys.forEach((key, index) => {
            const [section, metric] = key.split(".");
            const sectionData = this.getSection(stats, section);
            const points = sectionData ? sectionData[metric] : null;
            if (!Array.isArray(points)) {
                return;
            }
            const labelKey = labelKeys[index] ?? "";
            series.push({
                label: labelKey ? translator.translate(labelKey) : "",
                translateKey: labelKey,
                points: points as TrendPoint[],
            });
        });

        if (series.length > 0) {
            this.renderTrend(canvas, series, resolveLegendEl);
        }
    }

    // Registrations/updates alone don't say much about actual user behavior —
    // likes/reads do. So a chart with engagement metrics (posts sections:
    // registrations+updates+likes+reads) starts with only likes/reads on,
    // while a chart without them (users: registrations+updates only) starts
    // with just registrations on. Keyed by translateKey rather than a fixed
    // dataset index since the two chart shapes carry a different series count.
    private isDefaultVisibleSeries(s: TrendSeries, series: TrendSeries[]): boolean {
        const hasEngagementMetrics = series.some(
            (other) => other.translateKey === "admin_dashboard_likes" || other.translateKey === "admin_dashboard_reads",
        );

        return hasEngagementMetrics
            ? s.translateKey === "admin_dashboard_likes" || s.translateKey === "admin_dashboard_reads"
            : s.translateKey === "admin_dashboard_registrations";
    }

    private renderTrend(
        canvas: HTMLCanvasElement,
        series: TrendSeries[],
        resolveLegendEl: (canvas: HTMLCanvasElement) => HTMLElement | null,
    ): void {
        const labels = series[0]?.points.map((point) => point.bucket) ?? [];
        // Filling the area under the line reads fine for one series but turns
        // into visual noise once multiple lines share the same canvas.
        const shouldFill = series.length === 1;

        const chart = new Chart(canvas, {
            type: "line",
            data: {
                labels,
                datasets: series.map((s, index) => ({
                    label: s.label,
                    data: s.points.map((point) => point.count),
                    borderColor: TREND_LINE_COLORS[index % TREND_LINE_COLORS.length],
                    backgroundColor: TREND_FILL_COLORS[index % TREND_FILL_COLORS.length],
                    tension: 0.25,
                    fill: shouldFill,
                    pointRadius: 2,
                    hidden: !this.isDefaultVisibleSeries(s, series),
                })),
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                devicePixelRatio: CHART_DEVICE_PIXEL_RATIO,
                // Headroom for maxValueLabelPlugin's peak-value text, drawn
                // 8px above the highest point — without this, a peak near
                // the very top of the chart area gets its label clipped by
                // Chart.js's default plugin clipping (barely visible, not a
                // z-index/stacking issue — canvas drawing has no such thing).
                layout: { padding: { top: 20 } },
                // The legend is rendered as real HTML instead (see
                // renderTrendLegend) so it can carry data-translate and stay
                // correct when the user switches language — Chart.js draws
                // its own legend on the canvas, which the translator can't
                // reach.
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
            },
        });

        this.charts.set(canvas, chart);
        this.renderTrendLegend(resolveLegendEl(canvas), series, canvas.dataset.chart ?? "");
    }

    // Chart.js's own legend toggles dataset visibility on click for free, but
    // that legend is disabled (see renderTrend) in favor of a translatable
    // HTML one, so the toggle behavior has to be reimplemented here — each
    // item is a real <button> (native keyboard/focus support) carrying the
    // chart+dataset it controls; DashboardListener routes clicks on it to
    // toggleLegendDataset() below.
    private renderTrendLegend(container: HTMLElement | null, series: TrendSeries[], chartKey: string): void {
        if (!container) {
            return;
        }

        while (container.firstChild) {
            container.removeChild(container.firstChild);
        }

        series.forEach((s, index) => {
            if (!s.translateKey) {
                return;
            }

            const swatch = document.createElement("span");
            swatch.className = "admin-dashboard__chart-legend-swatch";
            swatch.style.backgroundColor = TREND_LINE_COLORS[index % TREND_LINE_COLORS.length];

            const label = document.createElement("span");
            label.dataset.translate = s.translateKey;
            label.textContent = s.label;

            const button = document.createElement("button");
            button.type = "button";
            button.className = "admin-dashboard__chart-legend-btn";
            button.classList.toggle("is-hidden", !this.isDefaultVisibleSeries(s, series));
            button.dataset.chartKey = chartKey;
            button.dataset.datasetIndex = String(index);
            button.appendChild(swatch);
            button.appendChild(label);

            const item = document.createElement("li");
            item.className = "admin-dashboard__chart-legend-item";
            item.appendChild(button);
            container.appendChild(item);
        });
    }

    public toggleLegendDataset(button: HTMLButtonElement): void {
        const chartKey = button.dataset.chartKey;
        const datasetIndexRaw = button.dataset.datasetIndex;
        if (!chartKey || datasetIndexRaw === undefined) {
            return;
        }

        const datasetIndex = Number(datasetIndexRaw);
        for (const [canvas, chart] of this.charts.entries()) {
            if (canvas.dataset.chart !== chartKey) {
                continue;
            }
            const nextVisible = !chart.isDatasetVisible(datasetIndex);
            chart.setDatasetVisibility(datasetIndex, nextVisible);
            chart.update();
            button.classList.toggle("is-hidden", !nextVisible);
            break;
        }
    }

    private renderStatusBreakdown(canvas: HTMLCanvasElement, breakdown: Record<string, number>): void {
        const labels = Object.keys(breakdown);
        const values = labels.map((label) => breakdown[label] ?? 0);
        const linkBase = canvas.dataset.statusLinkBase;
        const statusParam = canvas.dataset.statusParam ?? "status";

        const chart = new Chart(canvas, {
            type: "bar",
            data: {
                labels,
                datasets: [
                    {
                        data: values,
                        backgroundColor: "#4a7ebb",
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                devicePixelRatio: CHART_DEVICE_PIXEL_RATIO,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                onClick: (_event, elements) => {
                    if (!linkBase || elements.length === 0) {
                        return;
                    }
                    const status = labels[elements[0].index];
                    if (!status) {
                        return;
                    }
                    const url = new URL(linkBase, window.location.origin);
                    url.searchParams.set(statusParam, status);
                    window.open(url.toString(), "_blank", "noopener");
                },
                onHover: (event, elements) => {
                    const target = event.native?.target;
                    if (target instanceof HTMLElement) {
                        target.style.cursor = linkBase && elements.length > 0 ? "pointer" : "default";
                    }
                },
            },
        });

        this.charts.set(canvas, chart);
    }
}
